import { FileTypes } from 'utils/constants';

export const EVIDENCE_STATUSES = Object.freeze(['supporting', 'issue', 'uncertain']);

export const isValidVideoEvidenceRange = ({ start, end } = {}) =>
    Number.isFinite(start) && start >= 0 && Number.isFinite(end) && end > start;

export const isVideoFile = (file) => file?.type?.startsWith('video/') || file?.type === 'application/mp4';
const validTime = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const textValue = (value) => (typeof value === 'string' ? value : '');

export const formatVideoTime = (seconds) => {
    if (!validTime(seconds)) return '';
    const total = Math.round(seconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const remainder = String(total % 60).padStart(2, '0');
    return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${remainder}` : `${minutes}:${remainder}`;
};

export const normalizeVideoTranscript = (transcript) => {
    const result = { text: '', segments: [], blocks: [] };
    const source = Array.isArray(transcript?.segments) ? transcript.segments : [];
    let speakerLabel = '';
    let newBlock = true;
    source.forEach((entry, index) => {
        if (!entry || typeof entry !== 'object') return;
        if (entry.is_speaker) {
            speakerLabel = textValue(entry.speaker_label);
            newBlock = true;
            return;
        }
        if (!textValue(entry.text).trim()) return;
        const speaker = textValue(entry.speaker_label) || speakerLabel;
        if (result.text && !/\s$/.test(result.text) && !/^\s/.test(entry.text)) result.text += ' ';
        const hasRange = validTime(entry.start) && validTime(entry.end) && entry.end > entry.start;
        const segment = {
            id: `${entry.id ?? 'segment'}-${index}`,
            text: entry.text,
            start: hasRange ? entry.start : null,
            end: hasRange ? entry.end : null,
            speakerLabel: speaker,
            charStart: result.text.length,
            charEnd: result.text.length + entry.text.length,
        };
        result.text += entry.text;
        result.segments.push(segment);
        let block = result.blocks[result.blocks.length - 1];
        if (newBlock || !block || block.speakerLabel !== speaker) {
            block = { id: `block-${index}`, start: segment.start, speakerLabel: speaker, segments: [] };
            result.blocks.push(block);
        }
        block.segments.push(segment);
        if (block.start === null && segment.start !== null) block.start = segment.start;
        speakerLabel = speaker;
        newBlock = false;
    });
    if (!result.segments.length) {
        const text = textValue(typeof transcript === 'string' ? transcript : transcript?.text);
        if (text.trim()) {
            const segment = {
                id: 'untimed',
                text,
                start: null,
                end: null,
                speakerLabel: '',
                charStart: 0,
                charEnd: text.length,
            };
            result.text = text;
            result.segments.push(segment);
            result.blocks.push({ id: 'untimed', start: null, speakerLabel: '', segments: [segment] });
        }
    }
    return result;
};

// Keep source offsets even when case folding or whitespace normalization changes length.
const searchableText = (text) => {
    let value = '';
    const starts = [];
    const ends = [];
    let offset = 0;
    for (const character of text) {
        const folded = /\s/.test(character) ? ' ' : character.replace(/[’‘]/g, "'").toLowerCase();
        if (folded === ' ' && value.endsWith(' ')) {
            ends[ends.length - 1] = offset + character.length;
        } else {
            value += folded;
            for (let i = 0; i < folded.length; i++) {
                starts.push(offset);
                ends.push(offset + character.length);
            }
        }
        offset += character.length;
    }
    return { value, starts, ends };
};

export const findTranscriptMatches = (text, query) => {
    const needle = searchableText(textValue(query)).value.trim();
    if (!needle) return [];
    const haystack = searchableText(textValue(text));
    const matches = [];
    let cursor = 0;
    let index;
    while ((index = haystack.value.indexOf(needle, cursor)) !== -1) {
        matches.push({ charStart: haystack.starts[index], charEnd: haystack.ends[index + needle.length - 1] });
        cursor = index + needle.length;
    }
    return matches;
};

export const normalizeVideoEvidence = (location, file, normalizedTranscript) => {
    const raw = typeof location === 'string' ? { highlight: location } : location || {};
    const quote = textValue(raw.quote) || textValue(raw.highlight);
    const transcript = normalizedTranscript || normalizeVideoTranscript(file?.transcript);
    const issueType = ['omission', 'violation'].includes(raw.issue_type) ? raw.issue_type : null;
    const rawStatus = textValue(raw.status).toLowerCase();
    const status =
        raw.recording_quality_problem === true
            ? 'uncertain'
            : EVIDENCE_STATUSES.includes(rawStatus)
              ? rawStatus
              : 'uncertain';
    let start = validTime(raw.start) && validTime(raw.end) && raw.end > raw.start ? raw.start : null;
    let end = start !== null ? raw.end : null;
    let speakerLabel = textValue(raw.speaker_label);

    // Legacy text locations have no explicit range. Repeated phrases are ambiguous.
    if (raw.start === undefined && raw.end === undefined && quote && file) {
        const matches = findTranscriptMatches(transcript.text, quote);
        if (matches.length === 1) {
            const match = matches[0];
            const segments = transcript.segments.filter(
                (segment) => segment.charStart < match.charEnd && segment.charEnd > match.charStart
            );
            if (
                segments.length &&
                segments.every(
                    (segment, index) =>
                        segment.start !== null &&
                        segment.end !== null &&
                        (index === 0 ||
                            (segment.start >= segments[index - 1].start && segment.end >= segments[index - 1].end))
                )
            ) {
                start = segments[0].start;
                end = segments[segments.length - 1].end;
                const speakers = [...new Set(segments.map((segment) => segment.speakerLabel).filter(Boolean))];
                if (!speakerLabel && speakers.length === 1) speakerLabel = speakers[0];
            }
        }
    }
    if (!file || (raw.file_id != null && String(raw.file_id) !== String(file.id))) {
        start = null;
        end = null;
    }
    return {
        id: raw.id,
        fileId: raw.file_id ?? file?.id,
        start,
        end,
        speakerLabel,
        status,
        issueType,
        quote,
        explanation: textValue(raw.explanation),
    };
};

export const collectVideoEvidence = ({ grading, criteria = [], file, allowUnscoped = false }) => {
    if (!file) return [];
    const transcript = normalizeVideoTranscript(file.transcript);
    return criteria.flatMap((criterion) => {
        const locations = grading?.[criterion.id]?.locations;
        if (!Array.isArray(locations)) return [];
        return locations.flatMap((location, index) => {
            if (!location) return [];
            const fileId = typeof location === 'object' ? location.file_id : undefined;
            if (fileId == null ? !allowUnscoped : String(fileId) !== String(file.id)) return [];
            return [
                {
                    ...normalizeVideoEvidence(location, file, transcript),
                    category: FileTypes.VIDEO,
                    id: `${criterion.id}-${index}`,
                    criterionId: criterion.id,
                    criterionTitle: criterion.title,
                },
            ];
        });
    });
};
